/**
 * Geo Adaptive Layer hook — Step 21.
 * Fetches /api/ux-geo — server reads Accept-Language from request headers.
 * DEV override: appends ?country=XX from URL search params (DEV only).
 * Timeout: 150ms. Cache: 60s (module-level singleton).
 * Fallback: isActive=false, default values.
 */

import { useState, useEffect, useRef } from "react";
import { getUxFlags } from "@/lib/uxFlags";

const FETCH_TIMEOUT_MS = 150;
const CACHE_TTL_MS = 60_000;

export type GeoCountry = "FR" | "DE" | "GB" | "US" | "CA" | "default";

export interface GeoAdaptiveResult {
  country: GeoCountry;
  currency: string;
  currencySymbol: string;
  cluster: string;
  priceLabel: string;
  isActive: boolean;
}

interface GeoCacheEntry {
  data: GeoAdaptiveResult;
  expiresAt: number;
}

let moduleCache: GeoCacheEntry | null = null;

function getCached(): GeoAdaptiveResult | null {
  if (!moduleCache || Date.now() >= moduleCache.expiresAt) {
    moduleCache = null;
    return null;
  }
  return moduleCache.data;
}

function setCached(data: GeoAdaptiveResult): void {
  moduleCache = { data, expiresAt: Date.now() + CACHE_TTL_MS };
}

const FALLBACK: GeoAdaptiveResult = {
  country: "default",
  currency: "€",
  currencySymbol: "€",
  cluster: "default",
  priceLabel: "From",
  isActive: false,
};

/**
 * Reads ?country=XX from the current URL, but ONLY in DEV builds.
 * Production: always returns null (override is ignored).
 */
function getDevCountryOverride(): string | null {
  if (!import.meta.env.DEV) return null;
  try {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const country = params.get("country");
    if (country && ["FR", "DE", "GB", "US", "CA"].includes(country)) {
      return country;
    }
  } catch {
    // Ignore — SSR or restricted env
  }
  return null;
}

export function useGeoAdaptive(): GeoAdaptiveResult {
  const [result, setResult] = useState<GeoAdaptiveResult>(FALLBACK);
  const fetched = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const flags = await getUxFlags();
      if (!flags.adaptiveUxEnabled || !flags.uxGeoEnabled) return;

      const cached = getCached();
      if (cached) {
        if (!cancelled) setResult(cached);
        return;
      }

      // Append DEV override as query param (server will consume it)
      const devOverride = getDevCountryOverride();
      const url = devOverride ? `/api/ux-geo?country=${devOverride}` : "/api/ux-geo";

      const ac = new AbortController();
      const timeoutId = setTimeout(() => ac.abort(), FETCH_TIMEOUT_MS);

      try {
        const r = await fetch(url, {
          credentials: "same-origin",
          signal: ac.signal,
        });
        clearTimeout(timeoutId);
        if (cancelled) return;
        if (r.status !== 200) return; // 204 or error → keep FALLBACK
        const data = (await r.json()) as {
          country: GeoCountry;
          currency?: string;
          currencySymbol?: string;
          cluster: string;
          priceLabel?: string;
        };
        const currencySymbol = data.currencySymbol ?? data.currency ?? "€";
        const next: GeoAdaptiveResult = {
          country: data.country ?? "default",
          currency: currencySymbol,
          currencySymbol,
          cluster: data.cluster ?? "default",
          priceLabel: data.priceLabel ?? "From",
          isActive: true,
        };
        setCached(next);
        if (!cancelled) setResult(next);
      } catch {
        clearTimeout(timeoutId);
        // Timeout or network error → stay on FALLBACK silently
      }
    };

    if (!fetched.current) {
      fetched.current = true;
      void run();
    }

    return () => { cancelled = true; };
  }, []);

  return result;
}
